import { Modal } from 'bootstrap'

import { Component, ElementRef, ViewChild } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { Model } from '@classes/Model'
import { EnvironmentManagerService } from '@services/environment-manager.service'
import { ModelService } from '@services/model.service'
import { ToastColor, ToastService } from '@services/toast.service'

const extOf = (f: File): string => '.' + f.name.split('.').pop()!.toLowerCase()
const POINT_CLOUD_EXTENSIONS = ['.las', '.laz', '.e57', '.ply']
const SPLAT_EXTENSIONS = ['.ply', '.splat', '.ksplat', '.spz']
const MESH_EXTENSIONS = ['.glb', '.gltf', '.bin', '.png', '.jpg', '.jpeg', '.stl']

@Component({
  selector: 'shared-modal-create-model',
  templateUrl: './create-model.component.html',
  styleUrls: ['./create-model.component.css'],
  standalone: false
})
export class CreateModelComponent {
  @ViewChild('fileInput') fileInput: ElementRef

  public cancelText = 'Cancel'
  public modal: Modal
  public submitColor: ToastColor = 'green'
  public submitText = 'Create'
  public title = 'Create Model'

  public onSubmit: CallableFunction
  public onCancel: CallableFunction

  public form: UntypedFormGroup
  public selectedFiles: File[] = []

  // Allowed characters: letters, numbers, spaces, underscore (_), period (.), colon (:), forward slash (/), equals (=), plus (+), hyphen (-), and at (@)
  public allowedPattern: string = '[a-zA-Z0-9\\s_.:/=+\\-@]*'

  public plyFileType: 'pointCloud' | 'splat' = 'pointCloud'
  public splatSizeWarning: string | null = null

  get isFormInvalid(): boolean {
    return this.form.invalid || !this.selectedFiles.length
  }

  get isInvalidFileSelection(): boolean {
    return this.selectedFiles.length > 1 && !this.selectedFiles.every(f => MESH_EXTENSIONS.includes(extOf(f)))
  }

  get isPointCloud(): boolean {
    return this.selectedFiles.some(f =>
      POINT_CLOUD_EXTENSIONS.includes(extOf(f)) &&
      !(extOf(f) === '.ply' && this.plyFileType === 'splat')
    )
  }

  get isMesh(): boolean {
    return this.selectedFiles.some(f => MESH_EXTENSIONS.includes(extOf(f)))
  }

  get isSplat(): boolean {
    return this.selectedFiles.some(f =>
      SPLAT_EXTENSIONS.includes(extOf(f)) ||
      (extOf(f) === '.ply' && this.plyFileType === 'splat')
    )
  }

  get hasPlyFile(): boolean {
    return this.selectedFiles.some(f => extOf(f) === '.ply')
  }

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _toastService: ToastService,
    private elementRef: ElementRef,
    public envManager: EnvironmentManagerService,
    public modelService: ModelService,
  ) {
    this.form = this._formBuilder.group({
      name: ['New Model', [Validators.required]],
      description: [''],
      files: ['', [Validators.required]],
      isCentered: true
    })

    // Subscribe to value changes for the name control to sanitize input
    this.form.get('name').valueChanges.subscribe(value => {
      let sanitized = value.replace(/[^a-zA-Z0-9\s_.:/=+\-@]/g, '')
      if (sanitized !== value) {
        this.form.get('name').setValue(sanitized, { emitEvent: false })
      }
    })
  }

  private validateForm(): boolean {
    if (this.isFormInvalid) {
      this._toastService.toast({
        title: 'Error',
        message: 'Invalid input in the model creation',
        color: 'red'
      })
      return false
    }
    return true
  }

  private validateFileSelection(): boolean {
    if (this.isInvalidFileSelection) {
      this._toastService.toast({
        title: 'Error',
        message: 'Multiple uploads are allowed only for mesh models',
        color: 'red'
      })
      return false
    }
    return true
  }

  private validatePointCloudSize(file: File): boolean {
    const maxSize = this.isPointCloud && (file.name.endsWith('.e57') || file.name.endsWith('.laz'))
      ? 107_374_182_400
      : 182_536_110_080

    if (file.size > maxSize) {
      const message = file.size > 107_374_182_400
        ? 'Invalid input: Point Cloud is too large. Please convert file to LAS and try again.'
        : 'Invalid input: Point Cloud is too large.'
      this._toastService.toast({ title: 'Error', message, color: 'red' })
      return false
    }
    return true
  }

  private validateSplatSize(file: File): boolean {
    const maxSize = 100 * 1024 * 1024 // 100MB in bytes
    if (file.size > maxSize) {
      this.splatSizeWarning = 'Splat file is too large. Maximum size is 100MB.'
      this._toastService.toast({
        title: 'Error',
        message: 'Invalid input: Splat file is too large. Maximum size is 100MB.',
        color: 'red'
      })
      return false
    }
    this.splatSizeWarning = null
    return true
  }

  submit(): boolean {
    if (!this.validateForm() || !this.validateFileSelection()) {
      return false
    }

    const { description, name, isCentered } = this.form.value
    const anchorMode: 'centered' | 'default' = isCentered ? 'centered' : 'default'
    const model = new Model(name, [], { description })

    if (this.isPointCloud) {
      if (!this.validatePointCloudSize(this.selectedFiles[0])) {
        return false
      }
      this.modelService.createPointCloud(this.selectedFiles[0], { description, name, anchorMode }).subscribe()
    } else if (this.isSplat) {
      if (!this.validateSplatSize(this.selectedFiles[0])) {
        return false
      }
      this.modelService.createModelWithS3(this.selectedFiles, { description, name, type: 'splat' }).subscribe()
    } else if (this.selectedFiles.length > 0) {
      this.modelService.createModelWithS3(this.selectedFiles, { description, name }).subscribe()
    }
    return true
  }

  onFileChange(files: FileList | File[]) {
    this.splatSizeWarning = null
    const list = Array.isArray(files) ? files : Array.from(files)

    const isMesh = (f: File) => MESH_EXTENSIONS.includes(extOf(f))
    const isSplat = (f: File) =>
      SPLAT_EXTENSIONS.includes(extOf(f)) ||
      (extOf(f) === '.ply' && this.plyFileType === 'splat')
    const isPoint = (f: File) =>
      POINT_CLOUD_EXTENSIONS.includes(extOf(f)) && !isSplat(f)

    // ─── choose files ─────────────────────────────
    if (list.some(isMesh)) {
      // mesh: merge and de‑dupe
      const merged = [
        ...this.selectedFiles.filter(isMesh),
        ...list.filter(isMesh)
      ]
      this.selectedFiles = merged.filter(
        (f, i, arr) => arr.findIndex(o => o.name === f.name) === i
      )
    } else {
      // pointCloud / splat: keep exactly one
      const pool = list.filter(isSplat).length
        ? list.filter(isSplat)
        : list.filter(isPoint)
      if (pool.length > 1) {
        this._toastService.toast({
          title: 'Error',
          message: 'Only one point‑cloud or splat file may be uploaded',
          color: 'red'
        })
      }
      this.selectedFiles = pool.slice(0, 1)
    }

    // ─── sync form control ────────────────────────
    const ctl = this.form.get('files')
    if (this.selectedFiles.length) {
      ctl.setValue(this.selectedFiles)
      ctl.setErrors(null)
    } else {
      ctl.setValue('')
      ctl.setErrors({ required: true })
    }
    ctl.markAsTouched()
    ctl.updateValueAndValidity()
  }

  removeFile(idx: number) {
    this.selectedFiles.splice(idx, 1)
    this.onFileChange([])
  }

  _submit() {
    if (this.onSubmit) {
      this.onSubmit()
    }

    if (this.submit()) {
      this.modal?.hide()
      this.ngOnDestroy()
    }
  }

  _cancel() {
    if (this.onCancel) {
      this.onCancel()
    }

    this.modal?.hide()
    this.ngOnDestroy()
  }

  ngOnDestroy() {
    this.elementRef.nativeElement.remove()
  }
}